import React, { useState, useEffect, useRef } from 'react';
import { MailPlus, Plus, SquarePen } from 'lucide-react';
import Badge from '../UI/Badge';
import { countSegments, notify, getPractice, setSecureData, userAccessToken } from '../../helpers';
import { Config } from '../../Config';
import axios from 'axios';
import { loadMessageTemplate } from '../../services/message';
import Spacing from '../Spacing';
import { Drawer } from 'vaul';
import { setMessageTemplatesData } from '../../Session';

export default function MessageTemplateModal(props) {
  const {
    reloadData = () =>{},
    reloadDataFilter = {},
    account_id = '',
    editModal=false,
    editValue={},
    tooltip=false,
    schedulerPage=false
  } = props;

  const [activeModal, setActiveModal] = useState(false);
  const [segments, setSegments] = useState(0);
  const [inputMessageBody, setInputMessageBody] = useState();
  const dropdownRef = useRef(null);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const defaultValues = {
    practice_id: account_id,
    title: editValue.templateName ? editValue.templateName : "",
    template_type: editValue.templateType ? editValue.templateType : "",
    body: editValue.messageBody ? editValue.messageBody : "",
  }
  const [formData, setFormData] = useState(defaultValues);

  const loadTemplate = async () => {   
    const response = await loadMessageTemplate(
      getPractice("account_id")
    );
    if (response) {
      await setSecureData(
        "message_templates",
        JSON.stringify(response.data.results)
      );  
      setMessageTemplatesData(response.data.results);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "body") {
      setFormData({
        ...formData,
        [name]: value,
      });
      setInputMessageBody(value);
      setSegments(countSegments(value));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const copyText = (value) => {
    navigator.clipboard.writeText(value);
    // notify("success", "Copied "+value, 0.4)

    const messageTextarea = document.querySelector('textarea[name="body"]');
    const startPaste = messageTextarea.selectionStart;
    const endPaste = messageTextarea.selectionEnd;

    const newText = messageTextarea.value.substring(0, startPaste) + value + messageTextarea.value.substring(endPaste, messageTextarea.value.length);

    setFormData({
      ...formData,
      body: newText
    });

    setInputMessageBody(newText);
    setSegments(countSegments(newText));
  };

  // Add form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
      try {
        e.target.disabled = true;
        const response = await axios.post(
          `${Config.notificationServerUrl}/message-transaction/create-template?account_id=${account_id}`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
              accept: "application/json",
              Authorization: `Bearer ${userAccessToken()}`,
            },
          }
        );
  
        if(response){
          setFormData({
            ...formData,        
            message_body: '',
          });

          if (response.detail) {
            notify('error', response.detail);
          } else { 
            notify("success", "Template Created Successfully");
            setFormData(defaultValues);
            reloadData(reloadDataFilter);
            setInputMessageBody('');
            setSegments(0);
            handleDrawerClose();
            loadTemplate();
          }

          e.target.disabled = false;
        }
      } catch (error) {    
        notify('error', error.message);
        e.target.disabled = false;
      }
      
  };

  // Edit form submit
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    
      try {
        e.target.disabled = true;
        const response = await axios.post(
          `${Config.notificationServerUrl}/message-transaction/update-template?account_id=${getPractice("account_id")}`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
              accept: "application/json",
              Authorization: `Bearer ${userAccessToken()}`,
            },
          }
        );
  
        if(response){
          setFormData({
            ...formData,        
            message_body: '',
          });

          if (response.detail) {
            notify('error', response.detail);
          } else { 
            notify("success", "Template Updated Successfully");
            setFormData(defaultValues);
            reloadData(reloadDataFilter);
            setInputMessageBody('');
            setSegments(0);            
            handleDrawerClose();
            loadTemplate();
          }

          e.target.disabled = false;
        }
      } catch (error) {    
        notify('error', error.message);
        e.target.disabled = false;
      }
      
  };

  useEffect(() => {
    setInputMessageBody(editValue.messageBody);
    if(editValue?.messageBody){
      setSegments(countSegments(editValue.messageBody));
    }
    if(editModal === true){
      setFormData({
        ...formData,        
        id: editValue.id,
        practice_id: account_id,
      });
    }
  }, [editValue, editModal]);

  const onSubmitHandler = editModal ? handleUpdateSubmit : handleSubmit;

  const handleDrawerOpen = () => {
    setActiveModal(!activeModal);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={setActiveModal} onClose={handleDrawerClose}>
        {editModal ? (
          <Drawer.Trigger className="docpro_icon_btn"
            onClick={() => handleDrawerOpen()}
          >
            <SquarePen width={18} height={18} />
            {tooltip && <span className="docpro_tip">Edit Template</span>}
          </Drawer.Trigger>
        ) : schedulerPage ? (
          <Drawer.Trigger className="docpro_fs_14" type='button' style={{color:"var(--vivid)",border:"none",background:"none",cursor:"pointer"}}
            onClick={() => handleDrawerOpen()}
          >
            <Plus height={18} width={18} /> Create New Templates
          </Drawer.Trigger>
        ) : (
          <Drawer.Trigger className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
            onClick={() => handleDrawerOpen()}
          >
            <MailPlus width={20} height={20} />
            <span>Add New Template</span>
          </Drawer.Trigger>
        )}
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 medium">
            <Drawer.Title className="sr-only">Template</Drawer.Title>
            <Drawer.Description className="sr-only">Message template details</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_auto">
              <div className="docpro_modal">
                <form onSubmit={onSubmitHandler} className='modal_height_100'>
                  <div>
                    {editModal ?
                      <h2 className="docpro_fs_24 docpro_mb_0">Edit Template</h2>
                      :
                      <h2 className="docpro_fs_24 docpro_mb_0">Add New Template</h2>
                    }
                    <Spacing lg="20" sm="20" />

                    <label className="docpro_fw_500 docpro_primary_color">
                      Template Name <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="text"
                      className="docpro_form_field size_lg"
                      name='title'
                      required
                      defaultValue={editValue.templateName}
                      onChange={handleChange}  
                    />
                    <Spacing lg="20" sm="20" />

                    <label className="docpro_fw_500 docpro_primary_color">
                      Template Type <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      className="docpro_form_field size_lg"
                      name='template_type'
                      defaultValue={editValue.templateType}
                      onChange={handleChange}  
                    />
                    <Spacing lg="20" sm="20" />

                    <label className="docpro_fw_500 docpro_primary_color">
                      Message Body <span className="docpro_red_color">*</span> <span className="docpro_badge docpro_badge_purple " onClick={() => copyText("[first_name] ")}>[first_name] </span> <span className="docpro_badge docpro_badge_purple " onClick={() => copyText("[last_name] ")} >[last_name] </span>
                    </label>
                    <textarea
                      required
                      minLength={2}
                      name="body"
                      className="docpro_form_field size_lg"
                      cols="30"
                      rows="5"
                      value={inputMessageBody}
                      onChange={handleChange}                         
                      placeholder="Write your message here..."
                    ></textarea>
                    <Spacing lg="5" sm="5" />
                    <span className="docpro_primary_color docpro_fw_500">
                      Total SMS{' '}
                      <Badge color="cyan" square>
                        {segments}
                      </Badge>
                    </span>

                    <Spacing lg="15" sm="15" />
                    <hr />
                    <Spacing lg="15" sm="15" />
                  </div>
                  <div className="docpro_modal_btns small_buttons docpro_mt_auto">
                    <button
                      className="docpro_bordered_btn docpro_fw_600 size_lg"
                      onClick={() => handleDrawerClose()}
                    >
                      Cancel
                    </button>
                    {editModal ?
                      <button type='submit' className="docpro_bordered_btn size_lg docpro_fw_600 docpro_white_color docpro_filed_btn">
                        <span>Update</span>
                      </button>
                    :
                      <button type='submit' className="docpro_bordered_btn size_lg docpro_fw_600 docpro_white_color docpro_filed_btn">
                        <span>Submit</span>
                      </button>
                    }
                  </div>
                </form>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
